'use client'

import { motion } from 'framer-motion'
import { ArrowRight, Clock, MapPin, Phone } from 'lucide-react'
import { getBranchOpenStatus, summariseWorkingHours } from '@/utils/branch'

export default function BranchCard({ branch, vendor, index = 0 }: { branch: any, vendor: any, index?: number }) {
  void vendor
  const status = getBranchOpenStatus(branch?.workingHours)
  const hoursRows = summariseWorkingHours(branch?.workingHours)
  const mapHref = branch?.latitude && branch?.longitude
    ? `https://www.google.com/maps/search/?api=1&query=${branch.latitude},${branch.longitude}`
    : branch?.address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(branch.address)}`
      : null

  const Wrapper: any = mapHref ? 'a' : 'div'
  const wrapperProps = mapHref ? { href: mapHref, target: '_blank', rel: 'noopener noreferrer' } : {}

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.05, 0.4), duration: 0.5, ease: 'easeOut' }}
      viewport={{ once: true }}
    >
      <Wrapper
        {...wrapperProps}
        className="group relative rounded-2xl p-6 flex flex-col cursor-pointer transition-colors hover:bg-white/5 h-full"
        style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-mono tabular-nums tracking-[0.2em]" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {String(index + 1).padStart(2, '0')}
          </div>
          <div
            className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em]"
            style={{ color: status.open ? 'rgba(110,231,183,0.95)' : 'rgba(255,255,255,0.5)' }}
          >
            <span
              className="inline-flex w-1.5 h-1.5 rounded-full"
              style={{ background: status.open ? '#34d399' : 'rgba(255,255,255,0.4)' }}
            />
            {status.open ? 'Нээлттэй' : 'Хаалттай'}
          </div>
        </div>

        <div className="mt-7">
          <h3
            className="font-semibold tracking-[-0.025em] leading-[0.95] text-white line-clamp-2"
            style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 32 }}
          >
            {branch.name}
          </h3>
          {branch.isDefault && (
            <div className="mt-2 text-[12px] uppercase tracking-[0.18em]" style={{ color: '#a8a8b3' }}>
              Үндсэн салбар
            </div>
          )}
        </div>

        <div className="mt-6 mb-5" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} />

        <ul className="space-y-3 text-[13px]" style={{ color: 'rgba(255,255,255,0.6)' }}>
          {branch.address && (
            <li className="flex gap-3">
              <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ opacity: 0.5 }} />
              <span className="leading-snug" style={{ color: 'rgba(255,255,255,0.85)' }}>{branch.address}</span>
            </li>
          )}
          {branch.phone && (
            <li className="flex gap-3">
              <Phone className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ opacity: 0.5 }} />
              <span className="tabular-nums" style={{ color: 'rgba(255,255,255,0.85)' }}>{branch.phone}</span>
            </li>
          )}
          {hoursRows.length > 0 && (
            <li className="flex gap-3">
              <Clock className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ opacity: 0.5 }} />
              <div className="flex-1 min-w-0 flex flex-col gap-1">
                {hoursRows.map((r, i) => (
                  <div key={i} className="flex items-baseline justify-between gap-3">
                    <span style={{ color: 'rgba(255,255,255,0.5)' }}>{r.day}</span>
                    <span className="tabular-nums" style={{ color: 'rgba(255,255,255,0.85)' }}>{r.time}</span>
                  </div>
                ))}
              </div>
            </li>
          )}
        </ul>

        <div className="mt-auto pt-7 flex items-center justify-end">
          <span
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[12px] font-medium transition-all group-hover:translate-x-0.5"
            style={{ background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            Чиглэл харах <ArrowRight className="h-3 w-3" />
          </span>
        </div>
      </Wrapper>
    </motion.div>
  )
}
